/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * Utility functions for handling localStorage with expiry dates
 */

interface StorageItem {
  value: any;
  expiry: number;
}

/**
 * Sets a value in localStorage with an expiry date
 * @param key - The localStorage key
 * @param value - The value to store
 * @param expiryDate - The exact date and time when the item expires (default: 1 hour from now)
 */
export const setWithExpiry = (
  key: string,
  value: any,
  expiryDate: Date = new Date(Date.now() + 60 * 60 * 1000)
): void => {
  try {
    const item: StorageItem = {
      value,
      expiry: expiryDate.getTime(),
    };
    localStorage.setItem(key, JSON.stringify(item));
  } catch (error) {
    console.error('Failed to set item in localStorage with expiry:', error);
  }
};

/**
 * Gets a value from localStorage if not expired, otherwise sets and returns the default value
 * @param key - The localStorage key
 * @param defaultValue - The default value to return if item is expired or doesn't exist
 * @param expiryDate - The exact date and time for the default value if it needs to be set (default: 1 hour from now)
 * @returns The stored value if not expired, or the default value
 */
export const getWithExpiry = (
  key: string,
  defaultValue: any,
  expiryDate: Date = new Date(Date.now() + 60 * 60 * 1000)
): any => {
  try {
    const itemStr = localStorage.getItem(key);

    if (!itemStr) {
      // Item doesn't exist, set default value and return it
      setWithExpiry(key, defaultValue, expiryDate);
      return defaultValue;
    }

    const item: StorageItem = JSON.parse(itemStr);
    const now = Date.now();

    if (now > item.expiry) {
      // Item has expired, remove it and set default value
      localStorage.removeItem(key);
      setWithExpiry(key, defaultValue, expiryDate);
      return defaultValue;
    }

    // Item exists and is not expired
    return item.value;
  } catch (error) {
    console.error('Failed to get item from localStorage with expiry:', error);
    // On error, set default value and return it
    setWithExpiry(key, defaultValue, expiryDate);
    return defaultValue;
  }
};

